import React from "react";
import RecordAvatar, { RecordImageKind } from "../RecordAvatar";
import CopyValue from "../CopyValue";
import RecordActions, { RecordAction } from "./RecordActions";
import StatStrip, { RecordStat } from "./StatStrip";
import classes from "./record.module.scss";

export interface RecordHeaderProps {
    /** The record's name — the page's one h1. */
    title: string;
    /**
     * The visual anchor: DateTile (missions, requests) or DocTile (finance
     * documents) from components/RecordRow. Wins over `image`.
     */
    anchor?: React.ReactNode;
    /** Photo / logo for orgs and people — initials when there is none. */
    image?: { src?: string | null; kind?: RecordImageKind; round?: boolean };
    /** The status pill, right after the name. */
    status?: React.ReactNode;
    /** Type / attribute chips after the status. */
    chips?: React.ReactNode;
    /** Live / ops badges beside the name. */
    badges?: React.ReactNode;
    /** What the record is and where. */
    subtitle?: React.ReactNode;
    /** Reference number, in mono type with a copy button. */
    reference?: string | null;
    /** Meta line parts (owner, parent link, since…). Empty parts are dropped. */
    meta?: React.ReactNode[];
    actions?: RecordAction[];
    /** How many actions show before "More" (default 4). */
    maxVisibleActions?: number;
    /** Full-width row under the identity — a stage stepper or a next-step line. */
    below?: React.ReactNode;
    stats?: RecordStat[];
    className?: string;
}

const isPresent = (part: React.ReactNode): boolean => part !== null && part !== undefined && part !== false && part !== "";

/**
 * Top of every detail page: anchor, name, status and chips, subtitle, a meta
 * line with the copyable reference, the actions, an optional row below, and
 * the stat strip.
 */
const RecordHeader: React.FC<RecordHeaderProps> = ({
    title, anchor, image, status, chips, badges, subtitle, reference, meta, actions, maxVisibleActions, below, stats, className,
}) => {
    const metaParts = (meta ?? []).filter(isPresent);
    const anchorNode = anchor ?? (image
        ? <RecordAvatar name={title} src={image.src} kind={image.kind} round={image.round} />
        : null);

    return (
        <header className={`${classes.header} ${className ?? ""}`}>
            <div className={classes.identityRow}>
                {anchorNode && <div className={classes.anchor}>{anchorNode}</div>}
                <div className={classes.identity}>
                    <div className={classes.titleLine}>
                        <h1 className={classes.title}>{title}</h1>
                        {badges}
                        {status}
                        {chips}
                    </div>
                    {isPresent(subtitle) && <div className={classes.subtitle}>{subtitle}</div>}
                    {(reference || metaParts.length > 0) && (
                        <div className={classes.meta}>
                            {reference && (
                                <span className={`${classes.metaItem} ${classes.reference}`}>
                                    <CopyValue value={reference} label="reference" />
                                </span>
                            )}
                            {metaParts.map((part, index) => (
                                <span key={index} className={classes.metaItem}>{part}</span>
                            ))}
                        </div>
                    )}
                </div>
                {actions && actions.length > 0 && <RecordActions actions={actions} maxVisible={maxVisibleActions} />}
            </div>
            {isPresent(below) && <div className={classes.below}>{below}</div>}
            {stats && <StatStrip stats={stats} />}
        </header>
    );
};

export default RecordHeader;
